import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/toast';
import { useQueryClient } from '@tanstack/react-query';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../Layout/Modal';
import useAuthContext from '../../hooks/useAuthContext';
import { addContactsLoadService } from '../../services/contacts/ContactsService';

const AddContactsModal = ({ contacts, isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();

  const [contactForms, setContactForms] = useState(() =>
    contacts.map((c) => ({ phone: c.phone, name: '', email: '' })),
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setContactForms(contacts.map((c) => ({ phone: c.phone, name: '', email: '' })));
  }, [contacts]);

  const handleFieldChange = (index, field, value) => {
    setContactForms((prev) =>
      prev.map((form, i) => (i === index ? { ...form, [field]: value } : form)),
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await addContactsLoadService({
        token: user?.access_token,
        contacts: contactForms.map((form) => ({
          phone: form.phone.replace(/\s/g, ''),
          name: form.name || undefined,
          email: form.email || undefined,
        })),
      });

      queryClient.invalidateQueries(['contacts-by-user']);

      toast({
        title: t('payments.addContacts.successToast'),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      sessionStorage.removeItem('pending_recharge_contacts');
      onSuccess();
    } catch {
      toast({
        title: 'Error al guardar contactos',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const count = contactForms.length;

  return (
    <Modal show={isOpen} onClose={onClose} size="lg" noCloserOutside>
      <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-800">
            {t('payments.addContacts.title', { count })}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('payments.addContacts.subtitle')}
          </p>
        </div>

        {/* Contact forms */}
        <div className="space-y-6 max-h-[55vh] overflow-y-auto pr-1">
          {contactForms.map((form, index) => (
            <div key={`contact-form-${form.phone}-${index}`} className="space-y-3">
              {/* Phone badge */}
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#E8F4F4]">
                  <UserCircleIcon className="h-6 w-6 text-[#187672]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">
                    {t('payments.addContacts.rechargedNumber')}
                  </p>
                  <span className="inline-block rounded-full bg-[#D4ECEA] px-3 py-0.5 text-sm font-semibold text-[#187672]">
                    +53 {form.phone}
                  </span>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t('payments.addContacts.contactName')}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                  placeholder={t('payments.addContacts.contactNamePlaceholder')}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-[#187672] focus:ring-1 focus:ring-[#187672]"
                  disabled={isSaving}
                />
              </div>

              {/* Email */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t('payments.addContacts.contactEmail')}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleFieldChange(index, 'email', e.target.value)}
                  placeholder={t('payments.addContacts.contactEmailPlaceholder')}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-[#187672] focus:ring-1 focus:ring-[#187672]"
                  disabled={isSaving}
                />
              </div>

              {index < contactForms.length - 1 && (
                <div className="border-t border-gray-100" />
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-full border border-gray-200 px-6 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
          >
            {t('payments.addContacts.skip')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-full bg-[#187672] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#145f5c] disabled:opacity-60"
          >
            {isSaving
              ? t('payments.addContacts.saving')
              : t('payments.addContacts.save', { count })}
          </button>
        </div>
      </div>
    </Modal>
  );
};

AddContactsModal.propTypes = {
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      phone: PropTypes.string.isRequired,
      email: PropTypes.string,
    }),
  ).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddContactsModal;
